import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';

import { AppLogo } from '@/assets/icons/AppLogo';
import { Icons } from '@/assets/icons/icons';
import integrationsBg from '@/assets/images/whats-new/integrations-announcement.webp';
import atlassianLogo from '@/assets/images/whats-new/integrations/atlassian.jpg';
import calendlyLogo from '@/assets/images/whats-new/integrations/calendly.svg';
import githubLogo from '@/assets/images/whats-new/integrations/github.svg';
import intercomLogo from '@/assets/images/whats-new/integrations/intercom.jpg';
import linearLogo from '@/assets/images/whats-new/integrations/linear.jpg';
import mercuryLogo from '@/assets/images/whats-new/integrations/mercury.svg';
import notionLogo from '@/assets/images/whats-new/integrations/notion.jpg';
import posthogLogo from '@/assets/images/whats-new/integrations/posthog.svg';
import stripeLogo from '@/assets/images/whats-new/integrations/stripe.jpg';
import supabaseLogo from '@/assets/images/whats-new/integrations/supabase.svg';
import { cn } from '@/lib/utils';

type Brand = {
  id: string;
  name: string;
  src?: string;
  Component?: React.ComponentType<{ className?: string }>;
};

const BRANDS: Brand[] = [
  { id: 'slack', name: 'Slack', Component: Icons.slack },
  { id: 'googleDrive', name: 'Google Drive', Component: Icons.googleDrive },
  { id: 'notion', name: 'Notion', src: notionLogo },
  { id: 'atlassian', name: 'Atlassian', src: atlassianLogo },
  { id: 'linear', name: 'Linear', src: linearLogo },
  { id: 'github', name: 'GitHub', src: githubLogo },
  { id: 'stripe', name: 'Stripe', src: stripeLogo },
  { id: 'intercom', name: 'Intercom', src: intercomLogo },
  { id: 'posthog', name: 'PostHog', src: posthogLogo },
  { id: 'supabase', name: 'Supabase', src: supabaseLogo },
  { id: 'mercury', name: 'Mercury', src: mercuryLogo },
  { id: 'calendly', name: 'Calendly', src: calendlyLogo },
];

type Slot = { top: string; left: string };
const SLOTS: Slot[] = [
  { top: '17%', left: '0%' },
  { top: '14%', left: '24%' },
  { top: '21%', left: '46%' },
  { top: '34%', left: '64%' },
  { top: '50%', left: '74%' },
  { top: '66%', left: '64%' },
  { top: '79%', left: '46%' },
  { top: '86%', left: '24%' },
  { top: '83%', left: '0%' },
];

const SPOTLIGHT_INDEX = 4;
const ROTATE_INTERVAL_MS = 2400;

const BrandIcon = ({
  brand,
  className,
}: {
  brand: Brand;
  className?: string;
}) => {
  if (brand.Component) {
    return (
      <div
        className={cn(
          'flex items-center justify-center [&>*]:size-full [&_svg]:!size-full',
          className
        )}
      >
        <brand.Component />
      </div>
    );
  }
  return (
    <img
      src={brand.src}
      alt={brand.name}
      className={cn('object-contain', className)}
      draggable={false}
    />
  );
};

const ringCircleBaseClass =
  'flex size-full items-center justify-center rounded-full border border-white/60 bg-white shadow-[0_18px_24px_rgba(0,0,0,0.18)] ring-1 ring-black/5';
const RING_CIRCLE_SIZE = '14.7%';
const SLOT_TRANSITION =
  'top 0.8s cubic-bezier(0.215,0.61,0.355,1), left 0.8s cubic-bezier(0.215,0.61,0.355,1)';

export const IntegrationsSpotlight = () => {
  const reduceMotion = useReducedMotion();
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (reduceMotion) return;
    const id = window.setInterval(() => {
      setOffset((o) => (o + 1) % BRANDS.length);
    }, ROTATE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [reduceMotion]);

  const occupants = SLOTS.map((slot, i) => {
    const brand = BRANDS[(offset + i) % BRANDS.length];
    return { slot, brand, slotIndex: i };
  });

  return (
    <div className="relative size-full overflow-hidden bg-[#0b341f]">
      <img
        src={integrationsBg}
        alt=""
        className="pointer-events-none absolute inset-0 size-full object-cover"
        draggable={false}
      />

      <div
        className="absolute left-[-39%] top-1/2 -translate-y-1/2 rounded-full border border-white/10 bg-black/15 backdrop-blur-[2px]"
        style={{ width: '110%', aspectRatio: '1 / 1' }}
      />

      <div
        className="absolute left-[11%] top-1/2 h-[18%] w-[67%] -translate-y-1/2 border border-white/10 bg-black/15 backdrop-blur-sm"
        style={{
          // Elliptical left curve + explicit half-height right radius; rounded-r-full (9999px) crushes the left curve under CSS's proportional scaling
          borderTopLeftRadius: '24px',
          borderBottomLeftRadius: '24px',
          borderTopRightRadius: '57px',
          borderBottomRightRadius: '57px',
        }}
      />

      <svg
        className="pointer-events-none absolute left-[34%] top-1/2 h-px w-[40%] -translate-y-1/2 overflow-visible"
        viewBox="0 0 100 1"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient
            id="dashed-line-fade"
            gradientUnits="userSpaceOnUse"
            x1="0"
            y1="0.5"
            x2="100"
            y2="0.5"
          >
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop offset="20%" stopColor="white" stopOpacity="0.6" />
            <stop offset="55%" stopColor="white" stopOpacity="0.6" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
        <motion.line
          x1="0"
          y1="0.5"
          x2="100"
          y2="0.5"
          stroke="url(#dashed-line-fade)"
          strokeWidth="1.5"
          strokeDasharray="3 3"
          animate={reduceMotion ? undefined : { strokeDashoffset: [0, 6] }}
          transition={{ duration: 0.7, ease: 'linear', repeat: Infinity }}
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      <AnimatePresence>
        {occupants.map(({ brand, slot, slotIndex }) => (
          <div
            key={brand.id}
            className={cn(
              'absolute -translate-x-1/2 -translate-y-1/2',
              slotIndex === SPOTLIGHT_INDEX && 'z-10'
            )}
            style={{
              top: slot.top,
              left: slot.left,
              width: RING_CIRCLE_SIZE,
              aspectRatio: '1 / 1',
              transition: SLOT_TRANSITION,
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.45 }}
              className={ringCircleBaseClass}
            >
              <BrandIcon brand={brand} className="size-[60%]" />
            </motion.div>
          </div>
        ))}
      </AnimatePresence>

      <div
        className="absolute left-[24%] top-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[18%] border-2 border-[rgba(212,212,183,0.55)] bg-white shadow-[0_22px_30px_rgba(0,0,0,0.18)]"
        style={{ width: '19.3%', aspectRatio: '1 / 1' }}
      >
        <AppLogo size="65%" customColor="#0a0a0a" />
      </div>
    </div>
  );
};
